import { useState, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '../../../routes'
import { useResourceDocuments, ResourceDocumentFilters } from '../../../queries'

export const useResourceDocumentsList = () => {
  const query = useQuery<{
    page?: number
    queryString?: string
    status?: string
    category_id?: string
    featured?: boolean
  }>()

  const { search: locationSearch } = useLocation()
  const [cursor, setCursor] = useState<string | undefined>()

  // Build filters from query params
  const filters: ResourceDocumentFilters = useMemo(() => {
    const baseFilters: ResourceDocumentFilters = {
      limit: 12,
    }

    if (cursor) baseFilters.cursor = cursor
    if (query.search.queryString) baseFilters.search = query.search.queryString
    if (query.search.status) baseFilters.status = query.search.status
    if (query.search.category_id)
      baseFilters.category_id = query.search.category_id
    if (query.search.featured !== undefined)
      baseFilters.featured = query.search.featured

    return baseFilters
  }, [cursor, query.search])

  const { data, isLoading: loading, error } = useResourceDocuments(filters)

  const setQueryString = (queryString: string) => {
    setCursor(undefined) // Reset cursor when searching
    query.set({ ...query.search, queryString, page: 1 })
  }

  const setStatus = (status: string) => {
    setCursor(undefined)
    query.set({ ...query.search, status, page: 1 })
  }

  const setCategoryId = (category_id: string) => {
    setCursor(undefined)
    query.set({ ...query.search, category_id, page: 1 })
  }

  const setFeatured = (featured: boolean) => {
    setCursor(undefined)
    query.set({ ...query.search, featured, page: 1 })
  }

  const handleNextPage = () => {
    if (data?.page_info.has_next_page && data.page_info.end_cursor) {
      setCursor(data.page_info.end_cursor)
    }
  }

  const handlePreviousPage = () => {
    if (data?.page_info.has_previous_page && data.page_info.start_cursor) {
      setCursor(data.page_info.start_cursor)
    }
  }

  // Create facets with document counts for the UI
  const facets = useMemo(() => {
    if (!data?.nodes) return {}

    // Calculate counts for each category
    const categoryCounts = data.nodes.reduce((acc, doc) => {
      if (doc.category_name) {
        acc[doc.category_name] = (acc[doc.category_name] || 0) + 1
      }
      return acc
    }, {} as Record<string, number>)

    // Calculate counts for each status
    const statusCounts = data.nodes.reduce((acc, doc) => {
      if (doc.status) {
        acc[doc.status] = (acc[doc.status] || 0) + 1
      }
      return acc
    }, {} as Record<string, number>)

    // Calculate counts for featured/not featured
    const featuredCount = data.nodes.filter((doc) => doc.featured).length
    const notFeaturedCount = data.nodes.filter((doc) => !doc.featured).length

    return {
      category: categoryCounts,
      status: statusCounts,
      featured: {
        true: featuredCount,
        false: notFeaturedCount,
      },
    }
  }, [data?.nodes])

  const appliedFacets = useMemo(() => {
    const facets: string[] = []
    if (query.search.status) facets.push(`status:${query.search.status}`)
    if (query.search.category_id)
      facets.push(`category:${query.search.category_id}`)
    if (query.search.featured !== undefined)
      facets.push(`featured:${query.search.featured}`)
    return facets
  }, [query.search])

  const toggleFacet = (facet: string, action: 'remove' | 'add') => {
    const [facetType, facetValue] = facet.split(':')

    if (action === 'remove') {
      const updates: any = { ...query.search }
      if (facetType === 'status') delete updates.status
      if (facetType === 'category') delete updates.category_id
      if (facetType === 'featured') delete updates.featured
      query.set(updates)
    } else {
      const updates: any = { ...query.search, page: 1 }
      if (facetType === 'status') updates.status = facetValue
      if (facetType === 'category') updates.category_id = facetValue
      if (facetType === 'featured') updates.featured = facetValue === 'true'
      setCursor(undefined)
      query.set(updates)
    }
  }

  return {
    documents: data?.nodes || [],
    loading,
    error,
    queryString: query.search.queryString || '',
    setQueryString,
    setStatus,
    setCategoryId,
    setFeatured,
    facets,
    appliedFacets,
    toggleFacet,
    locationSearch,
    hasNextPage: data?.page_info.has_next_page || false,
    hasPreviousPage: data?.page_info.has_previous_page || false,
    handleNextPage,
    handlePreviousPage,
    totalCount: data?.total_count || 0,
  }
}
